import { parseQuery } from '@calendly/http-tools'

import { generatePaletteCulori } from 'initializers/theme/generate_token_color_theme.js'

import { generateTextColor } from './generate_text_color'
import { generateColorShades } from './generate_color_shades'
import { normalizeHexColor } from './normalize_text_color'
import { isDarkHexBackground } from './is_dark_background'

const BACKGROUND_COLOR_DEFAULT = 'ffffff'
const PRIMARY_COLOR_DEFAULT = '006bff'
const TEXT_COLOR_DEFAULT = '0a2540'

const convertHexToRGB = (hex) => hex.match(/.{2}/g).map((x) => parseInt(x, 16))

/** Subtle divider on branded surfaces (e.g. side panel); same alpha model as legacy text level 3. */
const subtleDividerOnSurfaceFromRgb = (rgbCommaSeparated) => `rgba(${rgbCommaSeparated}, 0.1)`

const BOOKING_CUSTOM_COLORS = 'data-booking-custom-colors'

const applyBookingCustomColorsFlag = (customColorsFromUrl) => {
  if (customColorsFromUrl) {
    document.documentElement.setAttribute(BOOKING_CUSTOM_COLORS, '1')
  } else {
    document.documentElement.removeAttribute(BOOKING_CUSTOM_COLORS)
  }
}

const BOOKING_CUSTOM_BG_IS_DARK = '--booking-custom-bg-is-dark'

/**
 * On dark custom embed backgrounds, default destructive red fails contrast; map error text to inverse standard.
 * Clears overrides when background is light or absent.
 */
const applyBookingDarkEmbedErrorContrast = (backgroundColorHex) => {
  const darkCustomBg = Boolean(backgroundColorHex) && isDarkHexBackground(backgroundColorHex)
  const roots = [document.documentElement, document.body].filter(Boolean)

  roots.forEach((el) => {
    el.style.setProperty(BOOKING_CUSTOM_BG_IS_DARK, darkCustomBg ? '1' : '0')
    if (darkCustomBg) {
      el.style.setProperty('--colorTextDestructive', 'var(--colorTextInverseStandard)')
      el.style.setProperty('--color-error', 'var(--colorTextInverseStandard)')
    } else {
      el.style.removeProperty('--colorTextDestructive')
      el.style.removeProperty('--color-error')
    }
  })

  if (darkCustomBg) {
    document.documentElement.setAttribute('data-booking-custom-bg-is-dark', '1')
  } else {
    document.documentElement.removeAttribute('data-booking-custom-bg-is-dark')
  }
}

const initTheme = ({ primaryColorHex, backgroundColorHex, textColorHex, customColorsFromUrl = false }) => {
  let variables = {}

  if (primaryColorHex || backgroundColorHex || textColorHex) {
    const colorPalette = generatePaletteCulori({
      backgroundColor: `#${backgroundColorHex ?? BACKGROUND_COLOR_DEFAULT}`,
      buttonColor: `#${primaryColorHex ?? PRIMARY_COLOR_DEFAULT}`,
      textColor: `#${textColorHex ?? TEXT_COLOR_DEFAULT}`
    })

    Object.entries(colorPalette).forEach(([name, value]) => {
      variables[`--${name}`] = value
    })
  }

  if (primaryColorHex) {
    const primaryColor = convertHexToRGB(primaryColorHex)
    const primaryColorRgb = primaryColor.join(', ')
    variables = {
      ...variables,
      '--primary-color': `rgb(${primaryColorRgb})`,
      '--primary-color-level2': `rgba(${primaryColorRgb}, 0.5)`,
      '--primary-color-level3': `rgba(${primaryColorRgb}, 0.15)`,
      '--primary-color-level4': `rgba(${primaryColorRgb}, 0.05)`,
      '--primary-text-color': generateTextColor(...primaryColor),
      '--primary-color-darker-10': generateColorShades(...primaryColor, -10),
      '--primary-color-darker-30': generateColorShades(...primaryColor, -30),
      '--text-color-secondary-4': generateTextColor(...primaryColor),
      '--text-color-secondary-3': `rgb(${primaryColorRgb})`
    }
  }
  if (backgroundColorHex) {
    const textColorRgb = '26, 26, 26'
    const subtleDividerOnSurface = subtleDividerOnSurfaceFromRgb(textColorRgb)
    variables = {
      ...variables,
      '--container-background-color': `#${backgroundColorHex}`,
      '--color-bg-white-l-1': `#${backgroundColorHex}`,
      '--text-color': `rgb(${textColorRgb})`,
      '--text-color-level2': `rgba(${textColorRgb}, 0.61)`,
      '--text-color-level3': subtleDividerOnSurface,
      '--booking-subtle-divider-on-surface': subtleDividerOnSurface
    }
  }
  if (textColorHex) {
    const textColorRgb = convertHexToRGB(textColorHex).join(', ')
    const subtleDividerOnSurface = subtleDividerOnSurfaceFromRgb(textColorRgb)
    variables = {
      ...variables,
      '--text-color': `rgb(${textColorRgb})`,
      '--text-color-level2': `rgba(${textColorRgb}, 0.6)`,
      '--text-color-level3': subtleDividerOnSurface,
      '--booking-subtle-divider-on-surface': subtleDividerOnSurface
    }
  }

  Object.entries(variables).forEach(([name, value]) => {
    document.documentElement.style.setProperty(name, value)
    if (document.body) {
      document.body.style.setProperty(name, value)
    }
  })

  applyBookingDarkEmbedErrorContrast(backgroundColorHex)
  applyBookingCustomColorsFlag(customColorsFromUrl)
}

const getThemeFromUrl = () => {
  const theme = parseQuery(window.location.search)
  const primaryColorHex = normalizeHexColor(theme.primary_color)
  const backgroundColorHex = normalizeHexColor(theme.background_color)
  const textColorHex = normalizeHexColor(theme.text_color)
  return { primaryColorHex, backgroundColorHex, textColorHex }
}

export const resetTheme = () => {
  const { primaryColorHex, backgroundColorHex, textColorHex } = getThemeFromUrl()

  // Reset theme if any theme is set in the URL
  if (primaryColorHex || backgroundColorHex || textColorHex) {
    initTheme({
      primaryColorHex: PRIMARY_COLOR_DEFAULT,
      backgroundColorHex: BACKGROUND_COLOR_DEFAULT,
      textColorHex: TEXT_COLOR_DEFAULT,
      customColorsFromUrl: false
    })
  }
}

export const initThemeFromUrl = () => {
  const { primaryColorHex, backgroundColorHex, textColorHex } = getThemeFromUrl()

  if (primaryColorHex || backgroundColorHex || textColorHex) {
    initTheme({ primaryColorHex, backgroundColorHex, textColorHex, customColorsFromUrl: true })
  }
}
